import posthog from 'posthog-js';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/Button';
import PageHeader from '../../components/PageHeader';
import PasswordInput from '../../components/PasswordInput';
import AppConfig from '../../config';
import { getDeviceInfo } from '../../hooks/useDeviceInfo';
import { t } from '../../lang';
import { pushSubscribe } from '../../serviceWorkerRegistration';
import HelperService from '../../services/HelperService';
import RequestService from '../../services/RequestService';
import WebSocketService from '../../services/WebSocketService';
import { appSlice } from '../../store/slices';
import { authSlice } from '../../store/slices/auth';

import './CreatePassword.scss';

const { routes } = AppConfig;

const PASSWORD_MIN_LENGTH = 6;
const PASSWORD_MAX_LENGTH = 64;

const CreatePassword = () => {
	const [password, setPassword] = useState('');
	const [repeatedPassword, setRepeatedPassword] = useState('');
	const [passwordErrorLabel, setPasswordErrorLabel] = useState(null);
	const [repeatedPasswordErrorLabel, setRepeatedPasswordErrorLabel] = useState(null);
	const [passwordsIdenticalErrorLabel, setPasswordsIdenticalErrorLabel] = useState(null);
	const [loading, setLoading] = useState(null);

	const publicConfig = useSelector(store => store.app.publicConfig);
	const username = useSelector(state => state.auth.username);
	const pin = useSelector(state => state.auth.pin);
	const captchaConfirmRequestData = useSelector(state => state.app.captchaConfirmRequestData);

	const intl = useIntl();
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const updatePassword = e => {
		setPassword(e.target.value);
		setPasswordErrorLabel(null);
		setPasswordsIdenticalErrorLabel(null);
	};

	const onBlurPassword = e => {
		const value = e.target.value.trim();
		setPassword(value);

		if (!value) return;

		if (value.length < PASSWORD_MIN_LENGTH) {
			setPasswordErrorLabel('Password must contain at least {number} characters');
			return;
		}
		if (/\s/.test(value) || !(/[a-z]/.test(value) && /[A-Z]/.test(value) && /\d/.test(value))) {
			setPasswordErrorLabel('Password is not valid');
			return;
		}
		if (repeatedPassword && value !== repeatedPassword) setPasswordsIdenticalErrorLabel("Password doesn't match");
	};

	const updateRepeatedPassword = e => {
		setRepeatedPassword(e.target.value);
		setRepeatedPasswordErrorLabel(null);
		setPasswordsIdenticalErrorLabel(null);
	};

	const onBlurRepeatedPassword = e => {
		const value = e.target.value.trim();
		setRepeatedPassword(value);

		if (!value) return;

		if (value.length < PASSWORD_MIN_LENGTH) {
			setRepeatedPasswordErrorLabel('Password must contain at least {number} characters');
			return;
		}
		if (/\s/.test(value) && !(/[a-z]/.test(value) && /[A-Z]/.test(value) && /\d/.test(value))) {
			setRepeatedPasswordErrorLabel('Password is not valid');
			return;
		}
		if (password && password !== value) setPasswordsIdenticalErrorLabel("Password doesn't match");
	};

	const onContinue = async () => {
		const resultHandler = response => {
			if (response?.Status === 'Success') {
				const token = response.Token;
				dispatch(appSlice.actions.setToken(token));
				dispatch(authSlice.actions.resetState());
				navigate(routes.mainPage, { replace: !!captchaConfirmRequestData });
				dispatch(appSlice.actions.setCaptchaConfirmRequestData());
				WebSocketService.DoConnection(publicConfig.apiUrl, token);
				HelperService.showNotification({
					type: 'success',
					title: t('Password and Pin-code have been created'),
				});
				pushSubscribe();

				posthog.capture('end_registration');
			}
		};

		const failHandler = error => {
			console.log('Error:', error);
		};

		try {
			setLoading(true);
			const deviceInfo = await getDeviceInfo();
			const { fingerprintId, deviceModel, deviceType, deviceVendor, osName, osVersion, browserName, browserVersion } =
				deviceInfo;

			const devicePayload = {
				Name: deviceModel || osName,
				Uuid: fingerprintId,
				Location: '',
				Type: deviceType === 'mobile' ? 1 : 2,
				PushSubscription: null,
			};

			const response = await RequestService.FinishRegistration(
				username,
				password,
				pin,
				devicePayload,
				resultHandler,
				failHandler
			);
			setLoading(false);
			resultHandler(response);
		} catch (error) {
			failHandler(error);
		}
	};

	const errorLabel = passwordErrorLabel || repeatedPasswordErrorLabel || passwordsIdenticalErrorLabel;

	return (
		<div className='CreatePassword'>
			<PageHeader
				header={'Create password'}
				subheader={'Create a password for account recovery. IMPORTANT: keep this password safe, and DO NOT lose it'}
			/>
			<div className='CreatePassword__body'>
				<div className='FormBlock'>
					<PasswordInput
						placeholder={intl.formatMessage({ id: 'Enter your password' })}
						value={password}
						onChange={updatePassword}
						onBlur={onBlurPassword}
						maxLength={PASSWORD_MAX_LENGTH}
						error={!!passwordErrorLabel}
						bottomSeparator
						autoFocus
					/>
					<PasswordInput
						placeholder={intl.formatMessage({ id: 'Repeat your password' })}
						value={repeatedPassword}
						onChange={updateRepeatedPassword}
						onBlur={onBlurRepeatedPassword}
						maxLength={PASSWORD_MAX_LENGTH}
						error={!!repeatedPasswordErrorLabel}
					/>
				</div>
				<div className='CreatePassword__password-error'>
					{errorLabel ? t(errorLabel, { number: PASSWORD_MIN_LENGTH }) : null}
				</div>
				<div className='CreatePassword__password-info'>
					{t(
						'The password must contain from {fromNumber} - {toNumber} characters, including lowercase and uppercase Latin letters and at least one number',
						{ fromNumber: PASSWORD_MIN_LENGTH, toNumber: PASSWORD_MAX_LENGTH }
					)}
				</div>
				<div className='CreatePassword__button-wrapper'>
					<Button
						className='CreatePassword__continue'
						onClick={onContinue}
						disabled={password && repeatedPassword && !!errorLabel}
						loading={loading}
					>
						{t('Continue')}
					</Button>
				</div>
			</div>
		</div>
	);
};

export default CreatePassword;
